import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { curatorQueries } from '../../query-factory';
import { useCuratorSnackbar } from './useCuratorSnackbar';

interface UseCouponLinkCopyOptions {
  couponNo: number | undefined;
  /** 호출자가 visitor 등 복사 기능을 노출하지 않는 컨텍스트일 때 prefetch 차단. */
  enabled?: boolean;
}

/**
 * 쿠폰 short link 를 mount 시점에 미리 fetch 해두고, click 시점에는
 * 캐시에서 동기 read → 즉시 `navigator.clipboard.writeText` 호출.
 *
 * iOS Safari / 일부 in-app webview 는 사용자 제스처 콜백 내부에서 `await`
 * 후 호출되는 clipboard API 를 NotAllowedError 로 reject 한다(1회차 실패,
 * 2회차는 react-query 캐시 hit 으로 우연히 성공하는 패턴이 재현됨).
 *
 * 본 hook 은 동일 도메인 `CurationCopyShortLink`/`ShopLinkCopyButton` 의
 * "사전 fetch + 동기 write" 패턴을 쿠폰 link 케이스에 일반화한다.
 *
 * 참고: https://www.reddit.com/r/webdev/comments/1ge5ou7/why_does_clipboard_access_fail_in_safari_after_an/
 */
export const useCouponLinkCopy = ({ couponNo, enabled = true }: UseCouponLinkCopyOptions) => {
  const snackbar = useCuratorSnackbar();
  const { data } = useQuery({
    ...curatorQueries.link.getLinkCoupon({ couponNo: couponNo ?? 0 }),
    enabled: enabled && couponNo != null,
  });

  return useCallback(() => {
    if (!data?.shortLink) {
      snackbar.error('단축링크를 가져올 수 없습니다.');
      return;
    }
    navigator.clipboard.writeText(data.shortLink);
    snackbar.success('링크가 복사되었습니다.');
  }, [data?.shortLink, snackbar]);
};
